<template>
  <liste-template>
    <template #header>
      <h1 class="title is-size-3">{{ $t('listeEcritureComptable.title')}}</h1>
      <breadcrumb :breadcrumb="breadcrumb"/>
    </template>
    <template #bar-left>
      <form @submit.prevent="recherche()" class="column is-narrow">
        <b-input
          v-model="criteresRecherche.auto"
          :placeholder="$t('general.button.search')"
          type="search" icon="magnify">
        </b-input>
      </form>
      <div class='column is-narrow'>
        <button class="button" @click='setShowFiltre'>
          <b-icon icon="filter-variant" size="is-small" type="is-default"></b-icon>
          <span class="is-size-7">
            {{ $t('general.filtre.title') }}
            <label class="has-text-weight-bold" :class="nombreCritere > 0 ? '' : 'is-invisible'">
              ({{ nombreCritere }})
            </label>
          </span>
        </button>
        <span @click="reinitialiserFiltre()"
          class="info-circle-right is-pulled-right is-cursor-pointer">
          <b-icon icon="close-circle" type="is-danger"></b-icon>
        </span>
      </div>
    </template>
    <template #bar-right>
      <div class="column is-narrow">
        <exportExcelButton
          :perm="constantes.ECRITURE_COMPTABLE.exporter"
          :query="exportExcelQuery"/>
      </div>
      <parametrage-table
        :reference-table="referenceTable"
        @endLoadParamtre="startArrayRendering = true">
      </parametrage-table>
    </template>
    <template #content>
      <v-server-table
        url=""
        :columns="getColumns"
        :options="options"
        @loaded="finLoading"
        @loading="debutLoading"
        :ref="referenceTable"
        v-if="startArrayRendering">
        <template slot='afterTable'>
          <table-items-in-view
            @input="$refs[referenceTable].setLimit($event)"
            :perPage="resultPerPage"/>
        </template>
        <template slot="ifEcriture.journal" slot-scope="props">
          {{ props.row.ifEcriture.journal }}
        </template>
        <template slot="ifEcriture.libelle" slot-scope="props">
          {{ props.row.ifEcriture.libelle }}
        </template>
        <template slot="ifEcriture.dateComptable" slot-scope="props">
          {{ constantes.formatDate(props.row.ifEcriture.dateComptable) }}
        </template>
        <template slot="ifEcriture.typeEcriture" slot-scope="props">
          {{ $t(getTypeEcriture(props.row.ifEcriture.typeEcriture)) }}
        </template>
        <template slot="libelle" slot-scope="props">
          {{ props.row.libelle }}
        </template>
        <template slot="compte" slot-scope="props">
          {{ props.row.compte }}
        </template>
        <template slot="debit" slot-scope="props">
          <div class="is-pulled-right">
            {{ constantes.formatDevise(props.row.debit) }}
          </div>
        </template>
        <template slot="credit" slot-scope="props">
          <div class="is-pulled-right">
            {{ constantes.formatDevise(props.row.credit) }}
          </div>
        </template>
        <template slot="dateDebut" slot-scope="props">
          {{ constantes.formatDate(props.row.dateDebut) }}
        </template>
        <template slot="dateFin" slot-scope="props">
          {{ constantes.formatDate(props.row.dateFin) }}
        </template>
        <template slot="compteAuxiliaire" slot-scope="props">
          {{ props.row.compteAuxiliaire }}
        </template>
        <template slot="ifEcriture.reference" slot-scope="props">
          <router-link-blank class="lien"
            v-if="getRoute(props.row.ifEcriture)"
            :to="getRoute(props.row.ifEcriture)"
            :message="props.row.ifEcriture.reference">
          </router-link-blank>
        </template>
        <template slot="ifEcriture.isValid" slot-scope="props">
          {{ props.row.ifEcriture.isValid ? $t('general.oui') : $t('general.non') }}
        </template>
        <template slot="ifEcriture.agenceId" slot-scope="props">
          {{ getAgenceLibelle(props.row.ifEcriture.agenceId) }}
        </template>
        <template slot="lot" slot-scope="props">
          <router-link-blank
            :to="{ name: 'c.comptabilite.lots_comptable',
              params: { id: props.row.ifEcriture.ifLotId }}"
                    :message="props.row.ifEcriture.ifLot.libelle"
              class="lien"
              v-if="props.row.ifEcriture.ifLotId">
          </router-link-blank>
        </template>
      </v-server-table>
    </template>
    <template #filter>
      <div class="componentBrief" v-show="showFiltre === true">
        <filtre-liste-ecriture-comptable
          ref="filtreListeEcritureComptable"
          @toggleFiltre="setShowFiltre"
          @recherche="makeRechercheFilter"/>
      </div>
    </template>
  </liste-template>
</template>
<script>
import { createNamespacedHelpers } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import { ECRITURE_COMPTABLE } from '@/configuration/compta/permissionsComptaConf';
import { SET_DETECT_CHANGE_SOCIETE, SET_LOCK_MENU } from '@/store/modules/societe-mutation-type';
import subscribeMutation from '@/services/store/subscribeMutation';
import { SET_CRITERE } from '@/store/modules/compta/ecritureComptable-mutation-type';
import exportExcelButton from '@/components/general/button/exportExcelButton';
import messageErreur from '@/services/general/messageErreur';
import formatDate from '@/services/general/formatDate';
import { formatDevise } from '@/services/general/filters/formatDevise';
import TableItemsInView from '@/components/general/TableItemsInView';
import routerLinkBlank from '@/components/general/routerLinkBlank';
import calculNombreCritere from '@/services/general/calculNombreCritere';
import {
  NO_PER_PAGE_VALUE,
  RESULTS_PER_PAGE,
  SORT_ICON,
  TEXTS,
} from '@/configuration/clientTableConf';
import formatIsoDate from '@/services/general/formatIsoDate';
import Breadcrumb from '@/components/general/breadcrumb';
import listeTemplate from '@/components/general/template/listeTemplate';
import * as ecritureConf from '@/configuration/compta/ecritureConf';
import filtreListeEcritureComptable from '@/components/compta/ecritureComptable/filtreListeEcritureComptable';
import generalTransformer from '@/services/general/filters/serverTableDataTransformer';
import parametrageTable from '@/components/general/parametrageTable';
import { REFERENCE_TABLE_LISTE_ECRITURE_COMPTABLE } from '@/configuration/general/referenceTableConf';
import {
  MODULE_BAIL,
  MODULE_TLPE,
  MODULE_ACHAT,
} from '@/configuration/devisConf';

const EcritureComptable = createNamespacedHelpers('ecritureComptable');
const Societe = createNamespacedHelpers('societe');

export default {
  name: 'l_LignesFactureContainer',
  data() {
    return {
      constantes: {
        ECRITURE_COMPTABLE,
        formatDate,
        formatDevise,
      },
      showFiltre: false,
      startArrayRendering: false,
      referenceTable: REFERENCE_TABLE_LISTE_ECRITURE_COMPTABLE,
      resultPerPage: RESULTS_PER_PAGE,
    };
  },
  components: {
    TableItemsInView,
    routerLinkBlank,
    Breadcrumb,
    listeTemplate,
    filtreListeEcritureComptable,
    parametrageTable,
    exportExcelButton,
  },
  computed: {
    ...EcritureComptable.mapGetters({
      ecrituresComptables: 'items',
      total: 'totalCount',
      criteresRecherche: 'criteresRecherche',
      lastPayload: 'lastPayload',
    }),
    ...Societe.mapGetters({
      societeActive: 'societeActive',
      societeActiveId: 'societeActiveId',
    }),
    breadcrumb() {
      return [
        { title: this.$t('general.breadcrumb.racine'), route: this.$PAGE_DEFAULT, isActive: false },
        { title: this.$t('listeEcritureComptable.title'), route: { name: 'l.bodac.factures-lignes' }, isActive: true },
      ];
    },
    getHeadings() {
      return this.columns.reduce((old, a) => ({ ...old, [a.name]: this.$i18n.t(a.text) }), {});
    },
    getSortable() {
      return this.columns.filter(c => c.sortable).map(c => c.name);
    },
    getColumns() {
      return this.columns.map(c => c.name);
    },
    options() {
      return {
        sortIcon: SORT_ICON,
        headings: this.getHeadings,
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: this.resultPerPage,
        texts: TEXTS,
        sortable: this.getSortable,
        columnsClasses: {
          listeIntervenants: 'ellipsis colonneListeIntervenants',
        },
        requestFunction: ((data) => {
          data.query = cloneDeep(this.criteresRecherche);
          if (data.query.tiers.length > 0) {
            data.query.tiers = data.query.tiers.map((tiers) => tiers.compteComptable);
          }
          data.query.dateComptable.dateDebut = formatIsoDate(data.query.dateComptable.dateDebut);
          data.query.dateComptable.dateFin = formatIsoDate(data.query.dateComptable.dateFin);
          data.query.societeId = this.societeActiveId;
          return this.actionsLoadEcritureComptable(data);
        }),
        responseAdapter: (() => {
          return {
            data: this.ecrituresComptables,
            count: this.total,
          };
        }),
      };
    },
    columns() {
      return [
        {
          name: 'ifEcriture.journal', text: 'listeEcritureComptable.tab.codeJournal', sortable: true,
        },
        {
          name: 'ifEcriture.dateComptable', text: 'listeEcritureComptable.tab.dateComptable', sortable: true,
        },
        {
          name: 'ifEcriture.libelle', text: 'listeEcritureComptable.tab.libelleEcriture', sortable: false,
        },
        {
          name: 'ifEcriture.typeEcriture', text: 'listeEcritureComptable.tab.typeEcriture', sortable: true,
        },
        {
          name: 'libelle', text: 'listeEcritureComptable.tab.libelleLigneEcriture', sortable: true,
        },
        {
          name: 'compte', text: 'listeEcritureComptable.tab.compteComptable', sortable: true,
        },
        {
          name: 'debit', text: 'listeEcritureComptable.tab.debit', sortable: false,
        },
        {
          name: 'credit', text: 'listeEcritureComptable.tab.credit', sortable: false,
        },
        {
          name: 'dateDebut', text: 'listeEcritureComptable.tab.dateDebut', sortable: false,
        },
        {
          name: 'dateFin', text: 'listeEcritureComptable.tab.dateFin', sortable: false,
        },
        {
          name: 'compteAuxiliaire', text: 'listeEcritureComptable.tab.compteAuxiliaire', sortable: true,
        },
        {
          name: 'ifEcriture.reference', text: 'listeEcritureComptable.tab.reference', sortable: false,
        },
        {
          name: 'ifEcriture.isValid', text: 'listeEcritureComptable.tab.etat', sortable: true,
        },
        {
          name: 'ifEcriture.agenceId', text: 'listeEcritureComptable.tab.agence', sortable: false,
        },
        {
          name: 'lot', text: 'listeEcritureComptable.tab.lot', sortable: false,
        },
      ];
    },
    nombreCritere() {
      const banCritereKey = ['auto', 'societeId', 'dateComptable'];
      let nombreCritere = calculNombreCritere(this.criteresRecherche, banCritereKey);
      if (this.criteresRecherche.dateComptable.dateDebut
        || this.criteresRecherche.dateComptable.dateFin) nombreCritere += 1;
      if (this.criteresRecherche.valide !== null) nombreCritere += 1;
      return nombreCritere <= 9 ? nombreCritere : '+9';
    },
    exportExcelQuery() {
      return {
        typeDonnee: ecritureConf.EXPORT_EXCEL_ECRITURE_COMPTABLE,
        typeExport: null,
        filtres: generalTransformer(this.lastPayload),
      };
    },
  },
  methods: {
    ...EcritureComptable.mapMutations({
      setCritereRecherche: SET_CRITERE,
    }),
    ...EcritureComptable.mapActions({
      actionsLoadEcritureComptable: 'loadEcritureComptable',
    }),
    ...Societe.mapMutations({
      setLockMenu: SET_LOCK_MENU,
    }),
    async setShowFiltre() {
      this.showFiltre = !this.showFiltre;
      if (this.showFiltre) await this.$refs.filtreListeEcritureComptable.initializeFiltre();
      this.setLockMenu(!this.lockMenu);
    },
    debutLoading() {
      if (this.loadingComponent) this.loadingComponent.close();
      this.loadingComponent = this.$buefy.loading.open();
    },
    finLoading() {
      this.loadingComponent.close();
    },
    recherche() {
      this.$refs[this.referenceTable].refresh();
    },
    makeRechercheFilter() {
      if (this.showFiltre) this.setShowFiltre();
      this.recherche();
    },
    getAgenceLibelle(agenceId) {
      return this.societeActive.agences.find(a => a.id === agenceId)?.libelle ?? '';
    },
    async reinitialiserFiltre() {
      await this.$refs.filtreListeEcritureComptable.initializeFiltre();
      await this.$refs.filtreListeEcritureComptable.viderCritere();
      await this.$refs.filtreListeEcritureComptable.appliquerCritere();
      this.showFiltre = false;
    },
    goFirstPage() {
      this.$refs[this.referenceTable].setPage(1);
    },
    getTypeEcriture(typeEcriture) {
      const type = ecritureConf.LISTE_TYPE.find((type) => {
        return type.code === typeEcriture;
      });
      return type?.libelle || '';
    },
    getRoute(ecriture) {
      const params = { id: ecriture.referencePiece };
      let name = '';
      switch (ecriture.typeEcriture) {
        case ecritureConf.TYPE_VENTE:
          name = 'c.bodac.factures'; break;
        case ecritureConf.TYPE_REGLEMENT:
          name = 'c.bodac.remises_bancaires'; break;
        case ecritureConf.TYPE_ACHAT:
          switch (ecriture.module) {
            case MODULE_BAIL:
              name = 'c.baux.facture_achat'; break;
            case MODULE_TLPE:
              name = 'c.gabed.titre_recette'; break;
            case MODULE_ACHAT:
              name = 'c.achat.factures'; break;
            default:
              name = 'c.baux.facture_achat'; break;
          }
          break;
        case ecritureConf.TYPE_PAIEMENT:
          switch (ecriture.module) {
            case MODULE_BAIL:
              name = 'c.baux.lot_paiement'; break;
            case MODULE_TLPE:
              name = 'c.gabed.lot_paiement_recette'; break;
            case MODULE_ACHAT:
              name = 'c.achat.lot_paiements_factures'; break;
            default:
              name = 'c.baux.lot_paiement'; break;
          }
          break;
        case ecritureConf.TYPE_OD:
          name = 'c.comptabilite.operation_diverse'; break;
        default:
          name = 'c.bodac.factures'; break;
      }
      return ({ name: name, params: params });
    },
  },
  beforeDestroy() {
    this.unsubscribeValue();
    this.setLockMenu(false);
  },
  async mounted() {
    const loadingComponent = this.$buefy.loading.open();
    try {
      this.unsubscribeValue = await subscribeMutation(this.$store, 'societe/' + SET_DETECT_CHANGE_SOCIETE, this.goFirstPage, this.recherche);
      this.setInformationPage(this.$t('pageCompta.ecritureComptable.liste'));
      loadingComponent.close();
    } catch (e) {
      messageErreur(this.$i18n);
      loadingComponent.close();
      throw e;
    }
  },
};
</script>
